import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Select from "react-select";
import { Spinner } from "react-bootstrap";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { getWarehouseKeyValueList } from "../../../services/index";
import { getActiveItemsByWarehouseid } from "../../../services/Settings/WarehouseService";
import { storeOptionsByWarehouseId } from "../../../services/Purchase/StoreService";
import { getRackByStore } from "../../../services/Purchase/RackService";
import { getCellByRack, getPalletByCell } from "../../../services/Purchase/CellService";
import { addStoreTransfer, getStoreTransferAvailableBatches } from "../../../services/Inventory/StoreTransferService";

// A whole batch row moves from its current location to a chosen store/rack/cell/pallet.
// Batches are never split or merged, so there is no quantity input and no "to batch".

const today = new Date().toISOString().substring(0, 10);

const emptyRow = () => ({
    item: null,
    batches: [],
    batchLoading: false,
    from: null,
    toStore: null,
    toRack: null,
    toCell: null,
    toPallet: null,
    rackOptions: [],
    cellOptions: [],
    palletOptions: [],
});

const toOptions = (res) => (res?.responsePacket || []).map((d) => ({ value: d.key, label: d.value }));
const na = (v) => v || "N/A";
const locLabel = (b) => [b.storeTitle, b.rackTitle, b.cellTitle, b.palletTitle].map(na).join(" / ");
const toLocLabel = (r) => [r.toStore?.label, r.toRack?.label, r.toCell?.label, r.toPallet?.label].map(na).join(" / ");
// Expired batches are not transferable; hide them even if the API returns them.
const isExpired = (b) => !!b.batchExpiredDate && String(b.batchExpiredDate).substring(0, 10) < today;
const batchKey = (b) => `${b.sourceType}:${b.sourceRecordId}`;
const batchOption = (b) => ({
    value: batchKey(b),
    label: `${b.batchNumber}  |  Qty ${b.availableQuantity}  |  ${locLabel(b)}`,
    data: b,
});

const StoreTransferNew = () => {
    const navigate = useNavigate();
    const [warehouseOptions, setWarehouseOptions] = useState([]);
    const [warehouse, setWarehouse] = useState(null);
    const [transferDate, setTransferDate] = useState(today);
    const [note, setNote] = useState("");
    const [itemOptions, setItemOptions] = useState([]);
    const [storeOptions, setStoreOptions] = useState([]);
    const [rows, setRows] = useState([emptyRow()]);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        getWarehouseKeyValueList().then((res) => {
            if (res.errorCode === 0) {
                const options = toOptions(res);
                setWarehouseOptions(options);
                if (options.length > 0) setWarehouse(options[0]);
            } else {
                toast.error(res.message);
            }
        });
    }, []);

    // Warehouse drives the item list and the store list. Changing it resets the rows.
    useEffect(() => {
        if (!warehouse?.value) return;
        setRows([emptyRow()]);
        getActiveItemsByWarehouseid({ page: 0, size: -1, search: "", uuid: warehouse.value }).then((res) => {
            if (res.errorCode === 0) {
                const list = res.responsePacket?.data || [];
                setItemOptions(list.map((d) => ({ value: d.itemId, label: d.itemTitle })));
            } else {
                toast.error(res.message);
            }
        });
        storeOptionsByWarehouseId(warehouse.value).then((res) => {
            if (res.errorCode === 0) {
                setStoreOptions(toOptions(res));
            } else {
                setStoreOptions([]);
                toast.error(res.message);
            }
        });
    }, [warehouse]);

    const updateRow = (index, patch) => {
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    };

    const handleItemChange = (index, option) => {
        updateRow(index, { item: option, batches: [], from: null, batchLoading: true });
        getStoreTransferAvailableBatches(warehouse.value, option.value).then((res) => {
            if (res.errorCode === 0) {
                const batches = (res.responsePacket || []).filter((b) => !isExpired(b));
                updateRow(index, { batches, batchLoading: false });
                if (batches.length === 0) {
                    toast(`No batch stock found for ${option.label} in this warehouse`);
                }
            } else {
                updateRow(index, { batchLoading: false });
                toast.error(res.message);
            }
        }).catch(() => {
            updateRow(index, { batchLoading: false });
            toast.error("Could not load batches");
        });
    };

    // Store -> Rack -> Cell -> Pallet cascade. Each level clears the levels below it.
    const handleStoreChange = (index, option) => {
        updateRow(index, { toStore: option, toRack: null, toCell: null, toPallet: null, rackOptions: [], cellOptions: [], palletOptions: [] });
        if (!option) return;
        getRackByStore(option.value).then((res) => {
            updateRow(index, { rackOptions: res.errorCode === 0 ? toOptions(res) : [] });
        }).catch(() => updateRow(index, { rackOptions: [] }));
    };

    const handleRackChange = (index, option) => {
        updateRow(index, { toRack: option, toCell: null, toPallet: null, cellOptions: [], palletOptions: [] });
        if (!option) return;
        getCellByRack(option.value).then((res) => {
            updateRow(index, { cellOptions: res.errorCode === 0 ? toOptions(res) : [] });
        }).catch(() => updateRow(index, { cellOptions: [] }));
    };

    const handleCellChange = (index, option) => {
        updateRow(index, { toCell: option, toPallet: null, palletOptions: [] });
        if (!option) return;
        getPalletByCell(option.value).then((res) => {
            updateRow(index, { palletOptions: res.errorCode === 0 ? toOptions(res) : [] });
        }).catch(() => updateRow(index, { palletOptions: [] }));
    };

    const addRow = () => setRows((prev) => [...prev, emptyRow()]);

    const removeRow = (index) => {
        if (rows.length === 1) {
            toast.error("At least one row is required");
            return;
        }
        setRows((prev) => prev.filter((_, i) => i !== index));
    };

    // Returns an error message when the form is incomplete, otherwise null.
    const validate = () => {
        if (!warehouse?.value) return "Select a warehouse";
        if (!transferDate) return "Select a transfer date";
        const seen = new Set();
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            const label = `row ${i + 1}`;
            if (!r.item) return `Select an item for ${label}`;
            if (!r.from) return `Select the batch to move for ${label}`;
            if (seen.has(r.from.value)) return `Batch ${r.from.data.batchNumber} is selected more than once (${label})`;
            seen.add(r.from.value);
            if (!r.toStore) return `Select the To store for ${label}`;
            if (locLabel(r.from.data) === toLocLabel(r)) return `From and To location are the same for ${label}`;
        }
        return null;
    };

    const handleSave = () => {
        const error = validate();
        if (error) {
            toast.error(error);
            return;
        }
        const payload = {
            warehouseId: warehouse.value,
            storeTransferDate: transferDate,
            note,
            items: rows.map((r) => ({
                itemId: r.item.value,
                sourceType: r.from.data.sourceType,
                sourceRecordId: r.from.data.sourceRecordId,
                toStoreId: r.toStore.value,
                toRackId: r.toRack?.value || null,
                toCellId: r.toCell?.value || null,
                toPalletId: r.toPallet?.value || null,
            })),
        };
        setSaving(true);
        addStoreTransfer(payload).then((res) => {
            if (res.errorCode === 0) {
                toast.success(res.message);
                navigate("/storeTransfer/list");
            } else {
                toast.error(res.message);
            }
        }).catch(() => toast.error("Save failed. Please try again."))
            .finally(() => setSaving(false));
    };

    const selectStyles = (px) => ({
        control: (base) => ({ ...base, minWidth: px }),
        menu: (base) => ({ ...base, minWidth: px, zIndex: 20 }),
    });

    return (
        <>
            <div className="p-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                    <h3 className="mb-0 font-size-18 font-weight-bolder text-uppercase">
                        <ArrowBackIcon style={{ cursor: "pointer" }} onClick={() => navigate("/storeTransfer/list")} />
                        &nbsp;New Store to Store Transfer
                    </h3>
                </div>

                <div className="row mb-3">
                    <div className="col-md-4">
                        <label>Warehouse <span className="text-danger">*</span></label>
                        <Select options={warehouseOptions} value={warehouse} onChange={setWarehouse} placeholder="Select Warehouse" />
                    </div>
                    <div className="col-md-3">
                        <label>Transfer Date <span className="text-danger">*</span></label>
                        <input type="date" className="form-control" value={transferDate} onChange={(e) => setTransferDate(e.target.value)} />
                    </div>
                    <div className="col-md-5">
                        <label>Note</label>
                        <input type="text" className="form-control" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Optional note" />
                    </div>
                </div>

                {rows.map((r, i) => {
                    const options = r.batches.map(batchOption);
                    return (
                        <div key={i} className="border rounded p-3 mb-3">
                            <div className="row align-items-end">
                                <div className="col-md-3">
                                    <label>Item <span className="text-danger">*</span></label>
                                    <Select
                                        options={itemOptions}
                                        value={r.item}
                                        placeholder="Select Item"
                                        onChange={(o) => handleItemChange(i, o)}
                                        styles={selectStyles(200)}
                                    />
                                </div>
                                <div className="col-md-5">
                                    <label>Batch to move <span className="text-danger">*</span></label>
                                    <Select
                                        options={options}
                                        value={r.from}
                                        isDisabled={!r.item}
                                        isLoading={r.batchLoading}
                                        placeholder={r.item ? "Select batch" : "Select item first"}
                                        onChange={(o) => updateRow(i, { from: o })}
                                        styles={selectStyles(300)}
                                    />
                                </div>
                                <div className="col-md-1">
                                    <label>Batch Qty</label>
                                    <input type="text" className="form-control" value={r.from ? r.from.data.availableQuantity : ""} disabled readOnly />
                                </div>
                                <div className="col-md-2">
                                    <label>Current location</label>
                                    <input type="text" className="form-control" value={r.from ? locLabel(r.from.data) : ""} disabled readOnly />
                                </div>
                                <div className="col-md-1 text-center">
                                    <button className="btn btn-default addbtn" title="Remove row" onClick={() => removeRow(i)}>
                                        <i className="fa fa-times"></i>
                                    </button>
                                </div>
                            </div>

                            <div className="row align-items-end mt-2">
                                <div className="col-md-3">
                                    <label>To Store <span className="text-danger">*</span></label>
                                    <Select
                                        options={storeOptions}
                                        value={r.toStore}
                                        isDisabled={!r.from}
                                        placeholder={r.from ? "Select store" : "Select batch first"}
                                        onChange={(o) => handleStoreChange(i, o)}
                                        styles={selectStyles(200)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label>To Rack</label>
                                    <Select
                                        options={r.rackOptions}
                                        value={r.toRack}
                                        isClearable
                                        isDisabled={!r.toStore}
                                        placeholder={r.toStore ? "Optional" : "Select store first"}
                                        onChange={(o) => handleRackChange(i, o)}
                                        styles={selectStyles(200)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label>To Cell</label>
                                    <Select
                                        options={r.cellOptions}
                                        value={r.toCell}
                                        isClearable
                                        isDisabled={!r.toRack}
                                        placeholder={r.toRack ? "Optional" : "Select rack first"}
                                        onChange={(o) => handleCellChange(i, o)}
                                        styles={selectStyles(200)}
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label>To Pallet</label>
                                    <Select
                                        options={r.palletOptions}
                                        value={r.toPallet}
                                        isClearable
                                        isDisabled={!r.toCell}
                                        placeholder={r.toCell ? "Optional" : "Select cell first"}
                                        onChange={(o) => updateRow(i, { toPallet: o })}
                                        styles={selectStyles(200)}
                                    />
                                </div>
                            </div>

                            {r.batches.length > 0 && (
                                <div className="mt-3" style={{ overflowX: "auto" }}>
                                    <table className="table table-sm table-bordered mb-1" style={{ fontSize: 13 }}>
                                        <thead>
                                            <tr>
                                                <th>Batch No</th>
                                                <th className="text-right">Batch Qty</th>
                                                <th>Mfg Date</th>
                                                <th>Exp Date</th>
                                                <th>Store</th>
                                                <th>Rack</th>
                                                <th>Cell</th>
                                                <th>Pallet</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {r.batches.map((b) => {
                                                const key = batchKey(b);
                                                const isFrom = r.from?.value === key;
                                                return (
                                                    <tr key={key} style={{ background: isFrom ? "#fff3cd" : undefined, cursor: "pointer" }}
                                                        onClick={() => updateRow(i, { from: batchOption(b) })}>
                                                        <td>{b.batchNumber} {isFrom && <b>(MOVING)</b>}</td>
                                                        <td className="text-right">{b.availableQuantity}</td>
                                                        <td>{b.batchManufacturingDate}</td>
                                                        <td>{b.batchExpiredDate}</td>
                                                        <td>{na(b.storeTitle)}</td>
                                                        <td>{na(b.rackTitle)}</td>
                                                        <td>{na(b.cellTitle)}</td>
                                                        <td>{na(b.palletTitle)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    {r.from && r.toStore && (
                                        <small className="text-muted">
                                            On approve, batch <b>{r.from.data.batchNumber}</b> (Qty {r.from.data.availableQuantity}) will move from <b>{locLabel(r.from.data)}</b> to <b>{toLocLabel(r)}</b>. Batch number, dates and reference stay the same.
                                        </small>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}

                <button className="btn btn-default addbtn mb-3" onClick={addRow}>
                    <AddIcon style={{ color: "blue" }} /> Add Row
                </button>

                <div className="d-flex justify-content-end">
                    <button className="btn btn-secondary mr-2" onClick={() => navigate("/storeTransfer/list")} disabled={saving}>
                        Cancel
                    </button>
                    <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                        {saving ? <Spinner animation="border" size="sm" /> : "Save (Pending)"}
                    </button>
                </div>
                <small className="text-muted d-block mt-2">
                    Saving creates the transfer as Pending. The batch moves only when it is approved from the list or the detail page.
                </small>
            </div>
            <Toaster />
        </>
    );
};

export default StoreTransferNew;
